import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import Papa from 'papaparse';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { grey, green, orange, red, blue } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { rateCharges, rateTransitText } from '../elariom/rateChargeText.js';

// Chip colours from the theme, so the same screens follow Elariom's own look inside Elariom.
const tint = key => ({ background: t => alpha(t.palette[key].main, 0.1), color: t => t.palette[key].dark });

// The rate agent on one import: start a run from the Imports table, and read what it produced.
// A run never publishes anything; it is the proposal a person reviews.

const monoSx = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' };

export function AgentCell({ row, accesstoken, onStatus, onOpenRun }) {
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);
    const last = (row.agentRuns || [])[0];
    const running = row.agentRunning && row.agentRunning.at > Date.now() - 15 * 60 * 1000;

    // While a run is in flight, check back every 10 seconds until it lands.
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(async () => {
            try {
                const { data } = await axios.get(`/api/admin/rate-imports/${row.id}/agent-status?accesstoken=${accesstoken}`);
                if (!data.agentRunning) onStatus(row.id, data);
            } catch (e) { /* keep polling */ }
        }, 10000);
        return () => clearInterval(t);
    }, [running, row.id, accesstoken, onStatus]);

    async function start() {
        setStarting(true);
        setError(null);
        try {
            const { data } = await axios.post(`/api/admin/rate-imports/${row.id}/agent-run?accesstoken=${accesstoken}`);
            onStatus(row.id, { agentRunning: data.agentRunning });
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setStarting(false);
    }

    if (running) {
        return (
            <Tooltip title={`Started ${dayjs(row.agentRunning.at).format('HH:mm')} by ${row.agentRunning.by}. A PDF can take a few minutes.`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: grey[700] }}><CircularProgress size={12} /> Running…</Box>
            </Tooltip>
        );
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
            {last && (
                <Button size='small' onClick={() => onOpenRun(row, last)} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: '12px', color: last.error ? red[700] : (last.validation?.problems?.length ? orange[900] : green[800]) }}>
                    {last.error ? 'Failed' : `${last.rateCount} rates · ${last.gapCount || 0} gaps`}
                </Button>
            )}
            <Tooltip title={row.transportationMethod ? 'Classify and parse this file. Nothing is published.' : 'Set the Mode first.'}>
                <span>
                    <Button size='small' disabled={starting || !row.transportationMethod || !row.rawFile} onClick={start}
                        sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: '12px' }}>
                        {starting ? 'Starting…' : last ? 'Run again' : 'Run agent'}
                    </Button>
                </span>
            </Tooltip>
            {error && <Typography sx={{ fontSize: '11px', color: red[700], maxWidth: 160 }}>{error}</Typography>}
        </Box>
    );
}

function Section({ title, count, children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{title}{count !== undefined && <span style={{ color: grey[600], fontWeight: 400 }}> ({count})</span>}</Typography>
            {children}
        </Box>
    );
}

// A file one of Monada's converters made into rates (metro trucking sheet, LCL tariff with CBM
// bands), with no agent: what it read, what it skipped, and a preview of the rates.
function ConverterResult({ run }) {
    const rates = run.rates || [];
    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip size='small' label={run.converter === 'metro-tariff' ? 'Metro trucking sheet' : 'LCL tariff with CBM bands'} sx={tint('info')} />
                <Chip size='small' label='Converted without the agent · no tokens' sx={tint('success')} />
                {run.mode && <Chip size='small' label={`Mode: ${run.mode}`} />}
                {run.supplier?.name && <Chip size='small' label={`Supplier: ${run.supplier.name}`} />}
                <Typography sx={{ fontSize: '12px', color: grey[600] }}>{rates.length} rates · {run.durationMs} ms</Typography>
            </Box>
            <Section title='What the converter did'>
                <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px' }}>{(run.notes || []).map((n, i) => <li key={i}>{n}</li>)}</Box>
            </Section>
            <Section title='Rows skipped' count={(run.errors || []).length}>
                {(run.errors || []).length === 0 && <Typography sx={{ fontSize: '13px', color: grey[600] }}>None.</Typography>}
                {(run.errors || []).slice(0, 50).map((e, i) => <Box key={i} sx={{ fontSize: '13px', borderLeft: `3px solid ${orange[300]}`, pl: 1 }}>Row {e.row + 1}: {e.reason}</Box>)}
            </Section>
            <Section title={`Rates${rates.length > 200 ? ' (first 200)' : ''}`} count={rates.length}>
                <Box sx={{ overflowX: 'auto', maxHeight: '420px', border: `1px solid ${grey[200]}`, borderRadius: '6px' }}>
                    <Box component='table' sx={{ borderCollapse: 'collapse', fontSize: '12px', '& td, & th': { border: `1px solid ${grey[200]}`, px: 1, py: '3px', whiteSpace: 'nowrap', verticalAlign: 'top' }, '& th': { background: grey[50], position: 'sticky', top: 0 } }}>
                        <thead><tr><th>From</th><th>To</th><th>Mode</th><th>Product</th><th>Valid</th><th>Transit</th><th>Charges</th></tr></thead>
                        <tbody>
                            {rates.slice(0, 200).map((r, i) => (
                                <tr key={i}>
                                    <td>{r.source?.text || 'Anywhere'}</td><td>{r.destination?.text || 'Anywhere'}</td><td>{r.transportationMethod}</td><td>{r.product?.type}</td>
                                    <td>{r.offer?.validFrom || '…'} → {r.offer?.validUntil || 'no end'}</td>
                                    <td>{rateTransitText(r)}</td>
                                    {/* The prices, not just the charge names, so they can be checked before publishing. */}
                                    <td>{rateCharges(r).map((c, j) => <div key={j}><span style={{ color: grey[600] }}>{c.title}:</span> {c.text}</div>)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            </Section>
        </>
    );
}

// A file that was already a Monada import CSV, checked by a script instead of the agent
// (src/server/rateAgent/fileChecks.js): what it fixed and what it found.
function ScriptCheckResult({ run, table }) {
    const fields = run.form?.sections?.[0]?.fields || [];
    const severity = { error: 'error', warning: 'warning', info: 'info' };
    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip size='small' label={run.converter === 'air-template' ? 'Air rate table' : 'Monada import CSV'} sx={tint('info')} />
                <Chip size='small' label='Checked by a script · no agent, no tokens' sx={tint('success')} />
                {run.mode && <Chip size='small' label={`Mode: ${run.mode}`} />}
                {run.supplier?.name && <Chip size='small' label={`Supplier: ${run.supplier.name}`} />}
                <Typography sx={{ fontSize: '12px', color: grey[600] }}>{run.rateCount} rows · {run.durationMs} ms</Typography>
            </Box>
            <Section title='Fixed automatically' count={run.checks?.fixes?.length}>
                {(run.checks?.fixes || []).length === 0 && <Typography sx={{ fontSize: '13px', color: grey[600] }}>Nothing needed fixing.</Typography>}
                <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px' }}>{(run.checks?.fixes || []).map((f, i) => <li key={i}>{f}</li>)}</Box>
            </Section>
            <Section title='Found' count={run.checks?.problems?.length}>
                {(run.checks?.problems || []).map((p, i) => <Alert key={i} severity={severity[p.severity] || 'info'} sx={{ py: 0 }}>{p.text}</Alert>)}
            </Section>
            <Section title={run.formSource === 'existing' ? 'Rate form (already in this workspace)' : 'Rate form (new, made from the file\'s columns)'} count={fields.length}>
                <Typography sx={{ ...monoSx, color: grey[700] }}>{run.form?.title}{run.form?.sections?.[0]?.defaultCurrency ? ` · default currency ${run.form.sections[0].defaultCurrency}` : ''}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {fields.map(f => <Chip key={f.id || f.title} size='small' label={`${f.title} · ${f.type}${f.guessed ? ' (guessed)' : ''}`} variant='outlined' sx={f.guessed ? { borderStyle: 'dashed' } : {}} />)}
                </Box>
            </Section>
            <Section title='Rates' count={run.rateCount}>
                {table && (
                    <Box sx={{ overflowX: 'auto', maxHeight: '420px', border: `1px solid ${grey[200]}`, borderRadius: '6px' }}>
                        <Box component='table' sx={{ borderCollapse: 'collapse', fontSize: '12px', '& td, & th': { border: `1px solid ${grey[200]}`, px: 1, py: '3px', whiteSpace: 'nowrap', maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }, '& th': { background: grey[50], position: 'sticky', top: 0 } }}>
                            <thead><tr>{table[0].map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                            <tbody>{table.slice(1).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} title={c}>{c}</td>)}</tr>)}</tbody>
                        </Box>
                    </Box>
                )}
            </Section>
        </>
    );
}

// How a recipe read the file: which column held what and how the price was built. Shown for runs
// read with a recipe (src/server/rateAgent/recipe.js) instead of the agent writing every rate.
function RecipeSection({ run }) {
    const r = run.recipe;
    if (!r) return null;
    const where = spec => (!spec ? 'not given' : spec.col ? `column ${spec.col}` : spec.value ? `"${spec.value}" for every row` : 'not given');
    const price = r.price || {};
    const priceText = price.model === 'weight-breaks'
        ? `minimum (${where(price.minimum)}) and per-kg prices by weight band: ${(price.breaks || []).map(b => `from ${b.fromKg} kg in column ${b.col}`).join(', ')}, built into one formula (rules R21, R22)`
        : price.model === 'per-container'
            ? `one price per container size: ${(price.containers || []).map(c => `${c.product} in column ${c.col}`).join(', ')}, each its own rate`
            : price.model === 'per-row'
                ? `one price per row in column ${price.priceCol}${price.product ? `, product from ${where(price.product)}` : ''}`
                : price.model === 'wm' ? `LCL by W/M: per cbm ${where(price.perCbm)}, per ton ${where(price.perTon)}, minimum ${where(price.minimum)}` : price.model;
    return (
        <Section title='How the file was read'>
            <Typography sx={{ fontSize: '13px' }}>
                {run.recipeSource === 'saved'
                    ? `With the recipe saved for this layout${run.recipeSavedAt ? ` on ${dayjs(run.recipeSavedAt).format('D MMM YYYY')}` : ''}. No model read the rates.`
                    : 'The agent read the table once and wrote this recipe; code applied it to every row. It is saved for this layout when the rates are published, so the next file in the same layout is read without a model.'}
            </Typography>
            <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <li>Rows {r.firstDataRow || '?'}{r.lastDataRow ? `–${r.lastDataRow}` : ' to the end'} of {(r.tables || []).join(', ') || 'the first table'}; titles in row {r.headerRow || '?'}.</li>
                <li>Origin: {where(r.origin)} ({r.originIs === 'name' ? 'place names, looked up' : 'codes'}){r.originCountry ? `, country ${where(r.originCountry)}` : ''}. Destination: {where(r.destination)} ({r.destinationIs === 'name' ? 'place names, looked up' : 'codes'}){r.destinationCountry ? `, country ${where(r.destinationCountry)}` : ''}.</li>
                <li>Valid from {where(r.validFrom)}, until {where(r.validUntil)}. Currency {where(r.currency)}. Transit {where(r.transitTime)}. Routing {where(r.transshipment)}. Supplier {where(r.supplier)}.</li>
                <li>{price.title || 'Price'}: {priceText}.</li>
                {(r.charges || []).length > 0 && <li>Other charges: {r.charges.map(c => `${c.title} (${c.type}, ${c.perContainer?.length ? 'a column per container size' : c.col ? `column ${c.col}` : c.value ? `"${c.value}"` : '—'})`).join('; ')}.</li>}
                {(r.remarkCols || []).length > 0 && <li>Remarks from columns {r.remarkCols.join(', ')}.</li>}
                {r.commonRemarks && <li>Notes for every rate: {r.commonRemarks}</li>}
            </Box>
        </Section>
    );
}

// What one agent run produced: its classification, decisions, proposed form, rates, what it
// held back and its gaps. Shared by the admin run dialog and Elariom's dropped-file runs.
export function AgentRunResult({ run, gapsNote }) {
    const table = useMemo(() => (run?.csv ? Papa.parse(run.csv, { header: false, skipEmptyLines: true }).data : null), [run]);
    if (run.kind === 'script') return <ScriptCheckResult run={run} table={table} />;
    if (run.kind === 'converter') return <ConverterResult run={run} />;
    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip size='small' label={`Category: ${run.classification?.category}`} sx={tint('info')} />
                {run.mode && <Chip size='small' label={`Mode: ${run.mode}${run.classification?.modeGiven === false ? ' (decided by the agent)' : ''}`} sx={run.classification?.modeGiven === false ? tint('warning') : undefined} />}
                <Chip size='small' label={`Confidence: ${run.classification?.confidence}`} sx={tint(run.classification?.confidence === 'high' ? 'success' : 'warning')} />
                <Chip size='small' label={`Pack: ${run.pack}`} />
                <Chip size='small' label={`Supplier: ${run.supplier?.name}${run.supplier?.matchedExisting ? ' (existing)' : ' (new)'}`} />
                <Typography sx={{ fontSize: '12px', color: grey[600] }}>
                    {run.usageByStep
                        // Whichever steps the run had: classify + parse, or classify + recipe (no
                        // model at all when a saved recipe was reused).
                        ? Object.entries(run.usageByStep).filter(([, u]) => u).map(([step, u]) => (u.model
                            ? `${step} on ${u.model}: ${(u.input || 0).toLocaleString()} in / ${(u.output || 0).toLocaleString()} out`
                            : `${step}: no model${step === 'recipe' ? ' (saved recipe)' : ''}`)).join(' · ')
                        : `${run.model} · ${run.usage?.input?.toLocaleString()} in / ${run.usage?.output?.toLocaleString()} out tokens`} · {Math.round((run.durationMs || 0) / 1000)}s · rules built {run.corpusVersion}
                </Typography>
            </Box>

            <RecipeSection run={run} />
            {(run.batches || []).length > 1 && (
                <Section title='Written in batches' count={run.batches.length}>
                    <Typography sx={{ fontSize: '12px', color: grey[600] }}>The file gave more rates than fit in one reply, so the agent planned the charges once and wrote the rates in parts, each reading the whole file.</Typography>
                    <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px' }}>
                        {run.batches.map((b, i) => <li key={i}>{b.label}: {b.ok ? `${b.rates} rates` : <span style={{ color: orange[900] }}>failed ({b.error})</span>}</li>)}
                    </Box>
                </Section>
            )}

            <Section title='Importer check'>
                {run.validation?.problems?.length
                    ? run.validation.problems.map((p, i) => <Alert key={i} severity='warning' sx={{ py: 0 }}>{p}</Alert>)
                    : <Alert severity='success' sx={{ py: 0 }}>All {run.validation?.rates} rates pass the importer.</Alert>}
            </Section>

            <Section title='Why it is this kind of file'>
                <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px' }}>{(run.classification?.signals || []).map((s, i) => <li key={i}>{s}</li>)}</Box>
            </Section>

            <Section title='Decisions' count={run.decisions?.length}>
                <Box component='ul' sx={{ m: 0, pl: 2.5, fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(run.decisions || []).map((d, i) => (
                        <li key={i}>{d.inferred && <Chip size='small' label='inferred' sx={{ height: '18px', fontSize: '10px', mr: 1, background: orange[50], color: orange[900] }} />}{d.text}</li>
                    ))}
                </Box>
            </Section>

            <Section title='Proposed rate form' count={run.form?.sections?.[0]?.fields?.length}>
                <Typography sx={{ ...monoSx, color: grey[700] }}>{run.form?.title} · default currency {run.form?.sections?.[0]?.defaultCurrency}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(run.form?.sections?.[0]?.fields || []).map(f => <Chip key={f.id} size='small' label={`${f.title} · ${f.type}`} variant='outlined' />)}
                </Box>
            </Section>

            <Section title='Rates' count={run.rateCount}>
                {table && (
                    <Box sx={{ overflowX: 'auto', maxHeight: '420px', border: `1px solid ${grey[200]}`, borderRadius: '6px' }}>
                        <Box component='table' sx={{ borderCollapse: 'collapse', fontSize: '12px', '& td, & th': { border: `1px solid ${grey[200]}`, px: 1, py: '3px', whiteSpace: 'nowrap', maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }, '& th': { background: grey[50], position: 'sticky', top: 0 } }}>
                            <thead><tr>{table[0].map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                            <tbody>{table.slice(1).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} title={c}>{c}</td>)}</tr>)}</tbody>
                        </Box>
                    </Box>
                )}
            </Section>

            <Section title='Held back' count={run.heldBack?.length}>
                {(run.heldBack || []).length === 0 && <Typography sx={{ fontSize: '13px', color: grey[600] }}>Nothing held back.</Typography>}
                {(run.heldBack || []).map((h, i) => (
                    <Box key={i} sx={{ fontSize: '13px', borderLeft: `3px solid ${orange[300]}`, pl: 1 }}><b>{h.what}</b><br />{h.reason}</Box>
                ))}
            </Section>

            <Section title='Gaps' count={run.gaps?.length}>
                {gapsNote && <Typography sx={{ fontSize: '12px', color: grey[600] }}>{gapsNote}</Typography>}
                {(run.gaps || []).map((g, i) => (
                    <Box key={i} sx={{ fontSize: '13px', borderLeft: `3px solid ${blue[200]}`, pl: 1 }}>
                        <b>{g.met}</b>{g.rulesSaid && <><br />Rules said: {g.rulesSaid}</>}<br />What it did: {g.whatIDid} <Chip size='small' label={g.confidence} sx={{ height: '18px', fontSize: '10px' }} />
                    </Box>
                ))}
            </Section>
        </>
    );
}

export function AgentRunDialog({ row, runSummary, accesstoken, onClose }) {
    const [run, setRun] = useState(runSummary.error ? runSummary : null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (runSummary.error) return;
        axios.get(`/api/admin/rate-imports/${row.id}/agent-runs/${runSummary.id}?accesstoken=${accesstoken}`)
            .then(({ data }) => setRun(data))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [row.id, runSummary, accesstoken]);

    return (
        <Dialog open={true} onClose={onClose} maxWidth='xl' fullWidth>
            <DialogTitle>
                Agent run
                <Typography sx={{ fontSize: '13px', color: grey[600] }}>
                    {row.organizationTitle} · {row.supplier || 'no supplier'} · {dayjs(runSummary.at).format('MMM D, YYYY HH:mm')} · {runSummary.by}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {error && <Alert severity='error'>{error}</Alert>}
                {runSummary.error && <Alert severity='error'>The run failed: {runSummary.error}</Alert>}
                {!run && !error && <CircularProgress size={24} />}
                {run && !run.error && <AgentRunResult run={run} gapsNote="These are also in the import's Gaps & corrections, to resolve there." />}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}
